import * as Sentry from '@sentry/react';
import { Context, ExtraContext } from '@utilitywarehouse/tracking-core';

import {
  ProductType,
  Quote,
  SelectCbcQuoteMutation,
} from '@generated/gql/graphql';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupCoreServicesEntity,
  useTrackSignupCbcAddedToBasketOnce,
} from '@tracking/trackingEvents';
import { getCbcPrice, getGroupCombinations } from '@tracking/utils';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';
import { findTrackingContextIndex } from '@tracking/utils/findTrackingContextIndex';
import { parseValueToPennies } from '@utils/price';

const updateTrackingContext = (
  contexts: ExtraContext<Context>[],
  selectResult: SelectCbcQuoteMutation
): ExtraContext<Context>[] => {
  const coreServiceContextIndex = findTrackingContextIndex(
    contexts,
    'signup_core_services'
  );

  // Should never happen
  if (coreServiceContextIndex === -1) {
    return contexts;
  }

  const coreServiceContext = contexts[coreServiceContextIndex]
    .data as SignupCoreServicesEntity;

  const {
    selectQuote: {
      monthlyCost: { value: totalPrice },
      quotes,
    },
  } = selectResult;

  const cbcQuote = quotes.find(
    (quote) => quote.product.type === ProductType.CashbackCard && quote.selected
  );

  // Should never happen
  if (!cbcQuote) {
    Sentry.captureMessage(
      'useTrackCbcSelected: data integrity flaw - no cbc quote found after selection'
    );
    return contexts;
  }

  if (!coreServiceContext.cbc_selected) {
    contexts[coreServiceContextIndex].data.cbc_selected = true;
  }

  const cbcPrice = getCbcPrice(cbcQuote as Quote);

  if (cbcPrice) {
    contexts[coreServiceContextIndex].data.cbc_price =
      parseValueToPennies(cbcPrice);
  }

  contexts[coreServiceContextIndex].data.total_price =
    parseValueToPennies(totalPrice);
  contexts[
    coreServiceContextIndex
  ].data.core_service_group_quantity_combinations =
    getGroupCombinations(coreServiceContext);

  return contexts;
};

const useTrackCbcSelected = () => {
  const { trackingContexts } = useTrackingContext();
  const trackCbcAddedToBasket = useTrackSignupCbcAddedToBasketOnce();

  const trackCbcSelected = (basketData: SelectCbcQuoteMutation) => {
    const updatedTrackingContexts = updateTrackingContext(
      trackingContexts,
      basketData
    );

    trackCbcAddedToBasket(
      {},
      {
        contexts: [...updatedTrackingContexts, ...buildOptimizelyEntity()],
      }
    );
  };

  return { trackCbcSelected };
};

export { useTrackCbcSelected };
